/**
 * The gate standing in a maze's exit or entry opening: a frame the width of the gap, a
 * glowing surface across it, and a sensor over the same volume that carries the player on
 * to the level beyond.
 *
 * The gate is a sensor, not a wall — you walk through it. What stops the player getting
 * *around* it is the geometry: the box `buildGrounds` hands over spans the whole opening
 * and overlaps the hedge run either side, so there is no crack of daylight to squeeze
 * past and nothing to jump over that the hedges do not already stop.
 *
 * The gate that *ends* a run looks different: an opaque red door fills the opening and a green
 * EXIT sign stands on the lintel above it, in place of the glow (design D5–D8 of
 * `openspec/changes/maze-start-courtyard-and-exit-door`). The unlit material moves with the job it
 * was doing: the glow's own material was unlit because this is the one thing in a maze that has to
 * be readable from the far end of a corridor at night, and a toon-shaded dark red door is a black
 * rectangle after dusk. So the door is lit with the hedges and the *sign* is the unlit one — which
 * is what a sign should be anyway, and it carries its meaning in the word rather than in the
 * green (principle 4).
 *
 * What happens in the collision callback is deliberately almost nothing: work out where
 * the gate leads and write it to the store. No level swap and no unmount happens here —
 * removing a collider while Rapier is iterating its intersections is how a physics engine
 * crashes. `App` commits the crossing later, from an effect, once the fade has covered it.
 * See the change's design D5.
 */

import { useMemo } from 'react';
import { CanvasTexture, DoubleSide, SRGBColorSpace } from 'three';
import { crossEntry, crossExit, isLeavingThroughGate } from '../game/progression';
import type { Box, GroundPoint } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';
import { getGradientMap, toonMaterial } from './materials';
import { PlayerSensor } from './PlayerSensor';
import {
  DOOR_RED,
  EXIT_SIGN_ASPECT,
  SIGN_GREEN,
  exitDoorCanvas,
  exitSignCanvas,
} from './textures';
import { useDisposable } from './useDisposable';

/** Which opening this gate stands in — which decides where it leads. */
export type GateRole = 'exit' | 'entry';

type Props = {
  gate: Box;
  role: GateRole;
  /** The level the gate stands in. The profile says what it leads to. */
  levelId: string;
  /** The objective walking out completes, on an exit gate that has one. */
  objectiveId?: string | null;
  /**
   * The gate's own arrival point — the maze side of the opening (design D2). Compared
   * against where the player was standing when they touched the gate, to tell an outward
   * crossing from an inward one.
   */
  inside: GroundPoint;
  /**
   * Whether walking out of here ends the run — which is what makes this gate a door instead of a
   * glowing opening. `App` works it out (`crossExit(profile, level.id) === 'complete'`, design D8);
   * the gate stays a function of its props.
   */
  finish?: boolean;
};

/** How thick the posts and lintel are. Enough to read as built, thin enough to walk between. */
const FRAME_THICKNESS = 0.35;
/** The frame's timber, and the light in the opening. Fixed: a gate is a device, not dressing. */
const FRAME_COLOR = '#2f2a3d';
const GLOW_COLOR = '#7fe8ff';

/** Thick enough to read as a leaf rather than a decal from an oblique angle. */
const DOOR_DEPTH = 0.12;
/** How wide the sign is; its height follows from the canvas's aspect, so the word is not stretched. */
const SIGN_WIDTH = 1.6;

export function PortalGate({ gate, role, levelId, objectiveId, inside, finish }: Props) {
  const { center, size } = gate;
  // The gap runs along whichever of the two horizontal axes is wider — the openings are in
  // the north and south hedge runs today, but nothing here needs to know that.
  const acrossX = size.x >= size.z;
  const width = acrossX ? size.x : size.z;
  const height = size.y;
  const frame = toonMaterial(FRAME_COLOR);

  // GPU resources, so they have to be the same objects across renders, and this component's to
  // release — the canvases underneath them are cached in `textures` and shared. Built only for a
  // finishing gate: every other gate in a run pays nothing for this.
  const maps = useDisposable(
    useMemo(() => {
      if (!finish) return null;
      const upload = (canvas: HTMLCanvasElement | null) => {
        if (!canvas) return null;
        const texture = new CanvasTexture(canvas);
        texture.colorSpace = SRGBColorSpace;
        return texture;
      };
      return { door: upload(exitDoorCanvas()), sign: upload(exitSignCanvas()) };
    }, [finish]),
  );

  /** A frame piece, sized along the opening's axis and placed in the gate's local space. */
  const piece = (
    length: number,
    tall: number,
    along: number,
    y: number,
  ): { position: [number, number, number]; args: [number, number, number] } => ({
    position: acrossX ? [center.x + along, y, center.z] : [center.x, y, center.z + along],
    args: acrossX
      ? [length, tall, FRAME_THICKNESS]
      : [FRAME_THICKNESS, tall, length],
  });

  const post = (side: 1 | -1) =>
    piece(FRAME_THICKNESS, height, (side * (width - FRAME_THICKNESS)) / 2, height / 2);
  const lintel = piece(width, FRAME_THICKNESS, 0, height - FRAME_THICKNESS / 2);

  // The door stands on the ground and goes up to the underside of the lintel; it is a shade wider
  // than the clear gap so it tucks behind the posts rather than leaving a seam of light at each
  // jamb. The sign stands on the lintel, flush with the face the player approaches.
  const doorWidth = width - FRAME_THICKNESS;
  const doorHeight = height - FRAME_THICKNESS;
  const signHeight = SIGN_WIDTH / EXIT_SIGN_ASPECT;

  // A `BoxGeometry`'s -z face is the mirror of its +z one, and a mirrored EXIT is worse than no
  // sign at all, so the door and the sign live in a group whose local +z is turned to face the
  // maze (design D10). For a maze's north exit that is already the case and the turn is zero.
  const insideAlong = acrossX ? inside.z - center.z : inside.x - center.x;
  const facing = (acrossX ? 0 : Math.PI / 2) + (insideAlong < 0 ? Math.PI : 0);

  return (
    <>
      {[post(1), post(-1), lintel].map(({ position, args }, index) => (
        <mesh
          key={index}
          name="gate-frame"
          position={position}
          material={frame}
          castShadow
          receiveShadow
        >
          <boxGeometry args={args} />
        </mesh>
      ))}

      {finish ? (
        <group position={[center.x, 0, center.z]} rotation={[0, facing, 0]}>
          {/* Opaque, lit, and still walked through — the same liberty the glow took, and a
              smaller one: a door you can walk through is less strange than a glowing cyan
              rectangle you can walk through. Non-collidable for the same reason the glow was:
              the sensor behind it spans the whole opening. */}
          <mesh name="exit-door" position={[0, doorHeight / 2, 0]} castShadow receiveShadow>
            <boxGeometry args={[doorWidth, doorHeight, DOOR_DEPTH]} />
            <meshToonMaterial
              map={maps?.door ?? null}
              color={maps?.door ? '#ffffff' : DOOR_RED}
              gradientMap={getGradientMap()}
            />
          </mesh>
          {/* Unlit, so the moonlight cannot dim it or band it into the hedges: this is the
              exit's visibility at night, inherited from the glow. */}
          <mesh name="exit-sign" position={[0, height + signHeight / 2, FRAME_THICKNESS / 2]}>
            <planeGeometry args={[SIGN_WIDTH, signHeight]} />
            <meshBasicMaterial
              map={maps?.sign ?? null}
              color={maps?.sign ? '#ffffff' : SIGN_GREEN}
            />
          </mesh>
        </group>
      ) : (
        /* The way through. Unlit and translucent on purpose: a toon material would band it
           into the hedges and lose it at night, and this is the one thing in the maze that
           has to be visible from the far end of a corridor. Double-sided, because you can
           come at it from either side. */
        <mesh
          name="portal-glow"
          position={[center.x, height / 2, center.z]}
          rotation={acrossX ? [0, 0, 0] : [0, Math.PI / 2, 0]}
        >
          <planeGeometry args={[width - FRAME_THICKNESS, height - FRAME_THICKNESS]} />
          <meshBasicMaterial color={GLOW_COLOR} transparent opacity={0.55} side={DoubleSide} />
        </mesh>
      )}

      {/* `PlayerSensor`, not a bare collider: the player capsule is kinematic and a sensor
          that does not say so never fires. That is exactly how this gate shipped broken
          the first time. */}
      <PlayerSensor
        box={gate}
        onEnter={(playerPosition) => {
          // A gate acts only on the way out. Rapier fires this once, on the touch, so
          // whichever side the player was standing on then decides the direction for
          // good — coming back through later fires it again, freshly.
          if (!isLeavingThroughGate(gate, inside, playerPosition)) return;

          const store = useGameStore.getState();
          const { profile } = store;
          if (!profile) return;

          // Completing and crossing are one act: the objective latches on the way through,
          // not at a separate sensor on the apron.
          if (role === 'exit' && objectiveId) store.reachExit(objectiveId);

          const destination =
            role === 'exit' ? crossExit(profile, levelId) : crossEntry(profile, levelId);
          if (destination === null) return;
          if (destination === 'complete') {
            store.dispatch('FINISH');
            return;
          }
          store.beginCrossing(destination);
        }}
      />
    </>
  );
}
